import bowser from "bowser";
import type {
  CapabilityBrowserInfo,
  CapabilityCodecInfo,
  CapabilityGpuInfo,
  CapabilityMemoryInfo,
  CapabilityNetworkInfo,
  CapabilitySocInfo,
  CapabilityViewportInfo,
  DeviceCapabilities,
  DeviceCapabilitiesPayload,
} from "../types/deviceCapabilities";
import type { CpuArchitecture } from "./deviceInfo";
import {
  buildDeviceInfo,
  getCpuArchitecture,
  getCpuCores,
  getDeviceType,
  getRamGB,
  getResolution,
  getThermalState,
  normalizeCpuArchitecture,
} from "./deviceInfo";

// ---- native bridges -------------------------------------------------------

interface NativeCapabilityBridge {
  getDeviceCpuCores?: () => number;
  getDeviceCpuArchitecture?: () => string; // Build.SUPPORTED_ABIS[0] / hw.machine
  getDeviceRamGB?: () => number;
  getDeviceCodecSupport?: () => string;
  getDeviceRamPressure?: () => string;
  getDeviceThermalState?: () => string;
  getDeviceThermalHeadroom?: () => number; // < 0 when the platform can't forecast
  getDeviceGpuInfo?: () => string; // JSON CapabilityGpuInfo
  getDeviceCodecCapabilities?: () => string; // JSON CapabilityCodecProfile[]
  getDeviceDisplayInfo?: () => string; // JSON CapabilityDisplayInfo
  getDeviceNetworkInfo?: () => string; // JSON CapabilityNetworkInfo
  getDeviceMemoryInfo?: () => string; // JSON CapabilityMemoryInfo
  getDeviceSocInfo?: () => string; // JSON CapabilitySocInfo
  getDeviceIdentification?: () => string; // JSON CapabilityDeviceIdentification
  getDevicePerformanceInfo?: () => string; // JSON NativeCapabilitySnapshot
  getPerformanceInfo?: () => string; // same, older bridge name
}

/** Window.DeviceBridge is already declared by ./deviceInfo — don't redeclare it. */
declare global {
  interface Window {
    TizenBridge?: NativeCapabilityBridge;
    WebOSBridge?: NativeCapabilityBridge;
    IOSBridge?: NativeCapabilityBridge;
    __IOSDevicePerformanceInfo?: string | NativeCapabilitySnapshot;
  }
}

interface NativeCapabilitySnapshot {
  cpuCores?: number;
  cpuArchitecture?: CpuArchitecture;
  reportedRamGB?: number;
  ramPressure?: string;
  thermalState?: string;
  thermalHeadroom?: number;
  gpu?: CapabilityGpuInfo;
  codecs?: CapabilityCodecInfo[];
  supportedDisplayModes?: string;
  network?: CapabilityNetworkInfo;
  memory?: CapabilityMemoryInfo;
  soc?: CapabilitySocInfo;
  boardPlatform?: string;
}

// ---- small helpers --------------------------------------------------------

function parseJson<T>(raw: string | undefined): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function asObject<T extends object>(value: unknown): T | undefined {
  return value !== null && typeof value === "object" ? (value as T) : undefined;
}

/** Spreading two absent blocks yields `{}` — collapse that back to undefined. */
function orUndefined<T extends object>(value: T): T | undefined {
  return Object.values(value).some((v) => v !== undefined) ? value : undefined;
}

function pruneUndefined<T extends object>(value: T): T {
  (Object.keys(value) as (keyof T)[]).forEach((key) => {
    if (value[key] === undefined) delete value[key];
  });
  return value;
}

// ---- native side ----------------------------------------------------------

// Bridge payloads use the legacy flat shape — convert on read.
interface LegacyBridgeSnapshot {
  cpuCores?: number;
  cpuArchitecture?: string;
  cpuAbi?: string; // older Android bridges name it after Build.CPU_ABI
  ramGB?: number;
  codec?: string;
  thermalState?: string;
  ramPressure?: string;
  thermalHeadroom?: number;
  gpu?: CapabilityGpuInfo;
  codecProfiles?: Array<{
    codec?: string;
    hardwareAccelerated?: boolean;
    maxBitrateMbps?: number;
    lowLatency?: boolean;
    guaranteed?: boolean;
  }>;
  display?: { supportedModes?: string };
  network?: CapabilityNetworkInfo;
  memory?: CapabilityMemoryInfo;
  soc?: CapabilitySocInfo;
  deviceIdentification?: { board?: { platform?: string } };
}

function legacyCodecsToNew(
  profiles: LegacyBridgeSnapshot["codecProfiles"],
): CapabilityCodecInfo[] | undefined {
  if (!profiles?.length) return undefined;
  return profiles
    .filter((p) => p.codec)
    .map((p) => ({
      name: p.codec!,
      hardwareAccelerated: p.hardwareAccelerated,
      maxBitrateMbps: p.maxBitrateMbps,
      lowLatency: p.lowLatency,
      guaranteed: p.guaranteed,
    }));
}

interface StaticBridgeReads {
  cpuCores: number | undefined;
  cpuArchitecture: string | undefined;
  ramGB: number | undefined;
  gpuJson: string | undefined;
  codecsJson: string | undefined;
  displayJson: string | undefined;
  identificationJson: string | undefined;
  socJson: string | undefined;
}

const staticBridgeReads = new WeakMap<
  NativeCapabilityBridge,
  StaticBridgeReads
>();

function readStatic(bridge: NativeCapabilityBridge): StaticBridgeReads {
  const cached = staticBridgeReads.get(bridge);
  if (cached) return cached;

  const reads: StaticBridgeReads = {
    cpuCores: bridge.getDeviceCpuCores?.(),
    cpuArchitecture: bridge.getDeviceCpuArchitecture?.(),
    ramGB: bridge.getDeviceRamGB?.(),
    gpuJson: bridge.getDeviceGpuInfo?.(),
    codecsJson: bridge.getDeviceCodecCapabilities?.(),
    displayJson: bridge.getDeviceDisplayInfo?.(),
    identificationJson: bridge.getDeviceIdentification?.(),
    socJson: bridge.getDeviceSocInfo?.(),
  };

  if (Object.values(reads).some((value) => value !== undefined)) {
    staticBridgeReads.set(bridge, reads);
  }
  return reads;
}

function readBridge(
  bridge: NativeCapabilityBridge | undefined,
): NativeCapabilitySnapshot {
  if (!bridge) return {};

  const stat = readStatic(bridge);

  const bundled =
    parseJson<LegacyBridgeSnapshot>(
      bridge.getDevicePerformanceInfo?.() ?? bridge.getPerformanceInfo?.(),
    ) ?? {};

  const headroom = asNumber(
    bridge.getDeviceThermalHeadroom?.() ?? bundled.thermalHeadroom,
  );

  const displayInfo =
    parseJson<{ supportedModes?: string }>(stat.displayJson) ?? bundled.display;

  const identInfo =
    parseJson<{ board?: { platform?: string } }>(stat.identificationJson) ??
    bundled.deviceIdentification;

  const rawCodecs =
    parseJson<LegacyBridgeSnapshot["codecProfiles"]>(stat.codecsJson) ??
    bundled.codecProfiles;

  return {
    cpuCores: asNumber(stat.cpuCores ?? bundled.cpuCores),
    cpuArchitecture: normalizeCpuArchitecture(
      asString(
        stat.cpuArchitecture ?? bundled.cpuArchitecture ?? bundled.cpuAbi,
      ),
    ),
    reportedRamGB: asNumber(stat.ramGB ?? bundled.ramGB),
    ramPressure: asString(
      bridge.getDeviceRamPressure?.() ?? bundled.ramPressure,
    ),
    thermalState: asString(
      bridge.getDeviceThermalState?.() ?? bundled.thermalState,
    ),
    thermalHeadroom:
      headroom !== undefined && headroom >= 0 ? headroom : undefined,
    gpu:
      parseJson<CapabilityGpuInfo>(stat.gpuJson) ??
      asObject<CapabilityGpuInfo>(bundled.gpu),
    codecs: legacyCodecsToNew(rawCodecs),
    supportedDisplayModes: asString(displayInfo?.supportedModes),
    network:
      parseJson<CapabilityNetworkInfo>(bridge.getDeviceNetworkInfo?.()) ??
      asObject<CapabilityNetworkInfo>(bundled.network),
    memory:
      parseJson<CapabilityMemoryInfo>(bridge.getDeviceMemoryInfo?.()) ??
      asObject<CapabilityMemoryInfo>(bundled.memory),
    soc:
      parseJson<CapabilitySocInfo>(stat.socJson) ??
      asObject<CapabilitySocInfo>(bundled.soc),
    boardPlatform: asString(identInfo?.board?.platform),
  };
}

/** Later entries win per name, so native detail lands on top of the web probe. */
function mergeCodecs(
  ...sources: (CapabilityCodecInfo[] | undefined)[]
): CapabilityCodecInfo[] | undefined {
  const byName = new Map<string, CapabilityCodecInfo>();
  for (const codecs of sources) {
    for (const codec of codecs ?? []) {
      if (!codec.name) continue;
      byName.set(codec.name, {
        ...byName.get(codec.name),
        ...pruneUndefined({ ...codec }),
      });
    }
  }
  return byName.size ? Array.from(byName.values()) : undefined;
}

function mergeSnapshots(
  ...snapshots: NativeCapabilitySnapshot[]
): NativeCapabilitySnapshot {
  const merged: NativeCapabilitySnapshot = {};
  for (const snapshot of snapshots) {
    const { gpu, codecs, network, memory, soc, ...scalars } = snapshot;
    Object.assign(merged, pruneUndefined({ ...scalars }));
    if (gpu) merged.gpu = { ...merged.gpu, ...pruneUndefined({ ...gpu }) };
    if (network)
      merged.network = { ...merged.network, ...pruneUndefined({ ...network }) };
    if (memory)
      merged.memory = { ...merged.memory, ...pruneUndefined({ ...memory }) };
    if (soc) merged.soc = { ...merged.soc, ...pruneUndefined({ ...soc }) };
    if (codecs?.length) merged.codecs = mergeCodecs(merged.codecs, codecs);
  }
  return merged;
}

function collectNative(): NativeCapabilitySnapshot {
  const iosGlobalRaw = window.__IOSDevicePerformanceInfo;
  const iosGlobal =
    typeof iosGlobalRaw === "string"
      ? parseJson<NativeCapabilitySnapshot>(iosGlobalRaw)
      : asObject<NativeCapabilitySnapshot>(iosGlobalRaw);

  return mergeSnapshots(
    readBridge(window.DeviceBridge as NativeCapabilityBridge | undefined),
    readBridge(window.TizenBridge),
    readBridge(window.WebOSBridge),
    readBridge(window.IOSBridge),
    iosGlobal ?? {},
  );
}

// ---- web side -------------------------------------------------------------

const CODEC_MIME: Record<string, string> = {
  h264: 'video/mp4; codecs="avc1.640028"',
  h265: 'video/mp4; codecs="hev1.1.6.L93.B0"',
  vp9: 'video/webm; codecs="vp09.00.10.08"',
  av1: 'video/mp4; codecs="av01.0.05M.08"',
};

const CODEC_PROBE_TARGETS = [
  { width: 1280, height: 720, framerate: 60, bitrate: 4_000_000 },
  { width: 1920, height: 1080, framerate: 60, bitrate: 8_000_000 },
  { width: 1920, height: 1080, framerate: 120, bitrate: 12_000_000 },
  { width: 3840, height: 2160, framerate: 60, bitrate: 25_000_000 },
] as const;

interface MediaCapabilitiesLike {
  decodingInfo?: (config: {
    type: string;
    video: {
      contentType: string;
      width: number;
      height: number;
      bitrate: number;
      framerate: number;
    };
  }) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;
}

let codecProfilesProbe: Promise<CapabilityCodecInfo[] | undefined> | undefined;

function probeCodecProfiles(): Promise<CapabilityCodecInfo[] | undefined> {
  if (!codecProfilesProbe) codecProfilesProbe = probeCodecProfilesOnce();
  return codecProfilesProbe;
}

async function probeCodecProfilesOnce(): Promise<
  CapabilityCodecInfo[] | undefined
> {
  const mc = (
    navigator as unknown as { mediaCapabilities?: MediaCapabilitiesLike }
  ).mediaCapabilities;
  if (typeof mc?.decodingInfo !== "function") return undefined;

  const byName = new Map<string, CapabilityCodecInfo>();
  const probes: Promise<void>[] = [];

  for (const [name, contentType] of Object.entries(CODEC_MIME)) {
    for (const target of CODEC_PROBE_TARGETS) {
      probes.push(
        mc
          .decodingInfo({
            type: "media-source",
            video: { contentType, ...target },
          })
          .then((result) => {
            if (result.supported) {
              const prev = byName.get(name);
              // prefer hardware-accelerated result across resolution probes
              if (!prev || result.powerEfficient) {
                byName.set(name, {
                  name,
                  hardwareAccelerated: result.powerEfficient === true,
                });
              }
            }
          })
          .catch(() => undefined),
      );
    }
  }

  await Promise.all(probes);
  return byName.size ? Array.from(byName.values()) : undefined;
}

let cachedGpuInfo: CapabilityGpuInfo | undefined;
let gpuInfoProbed = false;

function getWebGpuInfo(): CapabilityGpuInfo | undefined {
  if (gpuInfoProbed) return cachedGpuInfo;
  if (typeof document === "undefined") return undefined;
  gpuInfoProbed = true;
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return undefined;

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info") as {
      UNMASKED_VENDOR_WEBGL: number;
      UNMASKED_RENDERER_WEBGL: number;
    } | null;

    const renderer =
      (debugInfo &&
        asString(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL))) ||
      asString(gl.getParameter(gl.RENDERER));
    const vendor =
      (debugInfo &&
        asString(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL))) ||
      asString(gl.getParameter(gl.VENDOR));
    if (!renderer && !vendor) return undefined;

    cachedGpuInfo = { name: renderer, renderer, model: renderer, vendor };
    return cachedGpuInfo;
  } catch {
    return undefined;
  }
}

function getWebBrowserInfo(): CapabilityBrowserInfo {
  const parser = bowser.getParser(window.navigator.userAgent);
  const uaData = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData;
  return {
    name: asString(parser.getBrowserName()),
    version: asString(parser.getBrowserVersion()),
    engine: asString(parser.getEngineName()),
    platform: asString(navigator.platform),
    vendor: asString(navigator.vendor),
    uaPlatform: asString(uaData?.platform),
  };
}

export function getViewportInfo(
  target?: Element | null,
): CapabilityViewportInfo | undefined {
  if (typeof window === "undefined") return undefined;
  const rect = target?.getBoundingClientRect();
  const width = Math.round(rect?.width || window.innerWidth || 0);
  const height = Math.round(rect?.height || window.innerHeight || 0);
  if (!width || !height) return undefined;

  const aspect = Math.max(width, height) / Math.min(width, height);
  const resolution = getResolution(); // landscape short edge, in device px
  return {
    renderWidth: Math.round(aspect * resolution),
    renderHeight: resolution,
  };
}

interface NetworkInformationLike {
  type?: string;
  downlink?: number; // Mbps, rounded to 25kbps steps and capped at 10 by Chromium
}

function getWebConnection(): NetworkInformationLike | undefined {
  return (navigator as unknown as { connection?: NetworkInformationLike })
    .connection;
}

/** NetworkInformation.type — Chromium/Android only; desktop reports "unknown". */
function getWebTransport(): string | undefined {
  const type = getWebConnection()?.type;
  return type && type !== "unknown" ? type : undefined;
}

function getWebDownstreamKbps(): number | undefined {
  const downlink = getWebConnection()?.downlink;
  return typeof downlink === "number" && downlink > 0
    ? Math.round(downlink * 1000)
    : undefined;
}

function getWebRamPressure(): string | undefined {
  const memory = (
    performance as unknown as {
      memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number };
    }
  ).memory;
  if (
    typeof memory?.usedJSHeapSize !== "number" ||
    typeof memory?.jsHeapSizeLimit !== "number" ||
    memory.jsHeapSizeLimit <= 0
  ) {
    return undefined;
  }
  const ratio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
  if (ratio >= 0.85) return "high";
  if (ratio >= 0.6) return "medium";
  return "low";
}

/**
 * @param playerEl the stream surface, so `viewport.render*` reflects it
 */
export async function collectDeviceCapabilities(
  playerEl?: Element | null,
): Promise<DeviceCapabilitiesPayload> {
  const native = collectNative();
  const webCodecProfiles = await probeCodecProfiles();

  // make/model/os/resolution resolve exactly as they do for every other payload.
  const { make, model, os, resolution, type } = buildDeviceInfo(
    getDeviceType(),
  );

  // merged first: a native renderer string is a better arch hint than the web one
  const gpu = orUndefined({
    ...getWebGpuInfo(),
    ...pruneUndefined({ ...native.gpu }),
  });

  const spec: DeviceCapabilities = {
    hardware: orUndefined({
      cpu: orUndefined({
        cpuCores: native.cpuCores ?? getCpuCores(),
        architecture:
          native.cpuArchitecture ??
          getCpuArchitecture(gpu?.renderer ?? gpu?.name),
      }),
      memory: orUndefined({
        reportedRamGB: native.reportedRamGB ?? getRamGB(),
        availMemMB: native.memory?.availMemMB,
        totalMemMB: native.memory?.totalMemMB,
        ramPressure: native.ramPressure ?? getWebRamPressure(),
      }),
      gpu,
      soc: orUndefined({ ...native.soc }),
      board: orUndefined({ platform: native.boardPlatform }),
      codecs: mergeCodecs(webCodecProfiles, native.codecs),
      supportedDisplayModes: native.supportedDisplayModes,
      thermal: orUndefined({
        thermalState: native.thermalState ?? getThermalState(),
        thermalHeadroom: native.thermalHeadroom,
      }),
    }),
    browser: getWebBrowserInfo(),
    viewport: getViewportInfo(playerEl),
    network: orUndefined({
      transport: native.network?.transport ?? getWebTransport(),
      wifiFrequencyMHz: native.network?.wifiFrequencyMHz,
      downstreamKbps: native.network?.downstreamKbps ?? getWebDownstreamKbps(),
      upstreamKbps: native.network?.upstreamKbps,
    }),
  };

  return { make, model, type, resolution, os, spec };
}
